import React, { FC, ReactNode } from 'react';

import { useTheme } from 'styled-components';

import { CommonGCGame } from '../../../../types';
import { formatAsPercentage } from '../../../../utils';
import ProgressBar from '../../../progressBar/ProgressBar';

import {
    AdditionalInfo,
    AdditionalInfoContainer,
    SideBySideGameStatContainer,
    StatContainer,
    StatName,
    StatValue,
} from './styles';

type Props = {
    awayValue: string | number;
    homeValue: string | number;
    game: CommonGCGame;
    title: ReactNode;
    isPercentage: boolean;
    isPowerPlay: boolean;
};

const SideBySideStat: FC<Props> = ({ game, awayValue, homeValue, title, isPercentage, isPowerPlay }) => {
    const theme = useTheme();

    const getPercentageValue = (fraction) => {
        const fractionalParts = fraction.split('/');
        if (fractionalParts.length === 2) {
            if (Number(fractionalParts[1]) === 0) {
                return 0;
            }
            return (Number(fractionalParts[0]) / Number(fractionalParts[1])) * 100;
        }
        return fraction;
    };

    const getStatValueDisplay = (value: string | number): ReactNode => {
        if (isPowerPlay) {
            // we don't want to display "0%" if a team is 0/0 on power plays
            if (value === '0/0') {
                return ' - ';
            }
            return formatAsPercentage(getPercentageValue(value));
        }
        if (isPercentage) {
            return formatAsPercentage(Number(value) * 100);
        }
        return value;
    };

    return (
        <SideBySideGameStatContainer>
            <StatContainer>
                <StatValue>{getStatValueDisplay(awayValue)}</StatValue>
                <StatName>{title}</StatName>
                <StatValue $isHome>{getStatValueDisplay(homeValue)}</StatValue>
            </StatContainer>
            <ProgressBar
                awayTeamColor={theme.Team[game.awayTeam.abbrev]?.gamecenter || theme.Surface.Opposite.tertiary}
                awayTeamValue={isPowerPlay ? getPercentageValue(awayValue) : Number(awayValue)}
                homeTeamColor={theme.Team[game.homeTeam.abbrev]?.gamecenter || theme.Surface.Opposite.tertiary}
                homeTeamValue={isPowerPlay ? getPercentageValue(homeValue) : Number(homeValue)}
            />
            {isPowerPlay && (
                <AdditionalInfoContainer>
                    <AdditionalInfo>{awayValue}</AdditionalInfo>
                    <AdditionalInfo $isHome>{homeValue}</AdditionalInfo>
                </AdditionalInfoContainer>
            )}
        </SideBySideGameStatContainer>
    );
};

export default SideBySideStat;
